import { useCallback, useContext, useMemo, useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../ui/dropdown-menu";
import { Button } from "../../ui/button";
import { ChevronDown, ChevronUp } from "../../common/icons";
import { Eye, FileText, Notebook, ShieldOff, StickyNote, UserPlus, Users, XCircle } from "lucide-react";
import NewPostingModal from "../modals/NewPostingModal";
import ClientPostingsModal from "../modals/ClientPostingsModal";
import StaffMatchesModal from "../modals/StaffMatchesModal";
import NotesModal from "../modals/NotesModal";
import BlockedRelationshipsModal from "../modals/BlockedRelationshipsModal";
import DirectAddStaffModal from "../modals/DirectAddStaffModal";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { CurrentUserContext } from "@/context/CurrentUserContext";
import { useApiClient } from "@/context/ApiContext";
import { closePosting } from "@/services/staffing/Staffing";
import { useStaffing } from "@/context/StaffingContext";
import { getViewFromServiceType } from "@/utils/staffingUtil";


export interface IClientStaffingContextMenuProps {
    clientId: number;
    clientName: string | null;
    isPosting: boolean;
    isMatch: boolean;
    isWidget: boolean;
    serviceType?: string;
    postingId?: number;
}

interface IClientStaffingContextMenuOption {
    icon: JSX.Element;
    title: string;
    func: () => void;
}

const toInitials = (name: string | null) =>
    name?.split(' ').map(p => p[0]).join('').toUpperCase() ?? '';

const ClientStaffingContextMenu = ({ clientId, clientName, isPosting, isMatch, isWidget, serviceType, postingId }: IClientStaffingContextMenuProps) => {
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [postingModalOpen, setPostingModalOpen] = useState<boolean>(false);
    const [viewPostingsOpen, setViewPostingsOpen] = useState<boolean>(false);
    const [matchesModalOpen, setMatchesModalOpen] = useState<boolean>(false);
    const [notesModalOpen, setNotesModalOpen] = useState<boolean>(false);
    const [blockedRelationshipsOpen, setBlockedRelationshipsOpen] = useState<boolean>(false);
    const [directAddOpen, setDirectAddOpen] = useState<boolean>(false);
    const [closeDialogState, setCloseDialogState] = useState<null | 'confirm' | 'loading' | 'success' | 'error'>(null);
    const [closeError, setCloseError] = useState<string | null>(null);

    const { accessControl } = useContext(CurrentUserContext);
    const apiClient = useApiClient();
    const { refreshPostings } = useStaffing();

    const handleClosePosting = () => {
        if (!postingId) return;
        setCloseDialogState('loading');
        closePosting(postingId, apiClient)
            .then(() => {
                setCloseDialogState('success');
                if (serviceType) {
                    const view = getViewFromServiceType(serviceType);
                    refreshPostings(view);
                }
            })
            .catch(err => {
                const detail = err?.response?.data?.detail ?? err?.message ?? 'An unexpected error occurred.';
                setCloseError(detail);
                setCloseDialogState('error');
            });
    };

    const openClientFile = useCallback(() => {
        window.open(`https://clientform.teampbs.com/?ID=${clientId}`, '_blank');
    }, [clientId]);
    const viewPostings = () => {
        setViewPostingsOpen(true);
    };
    const menuOptions: IClientStaffingContextMenuOption[] = useMemo<IClientStaffingContextMenuOption[]>(() => {
        const options: IClientStaffingContextMenuOption[] = [];
        if (!isPosting) {
            options.push({
                icon: <StickyNote className="h-4 w-4" />,
                title: 'Post It!',
                func: () => setPostingModalOpen(true)
            });
        }
        if (!isWidget) {
            options.push({
                icon: <Eye className="h-4 w-4" />,
                title: 'Postings',
                func: viewPostings
            }, {
                icon: <FileText className="h-4 w-4" />,
                title: 'Notes',
                func: () => setNotesModalOpen(true)
            })

        }
        if (!isMatch) {
            options.push({
                icon: <Users className="h-4 w-4" />,
                title: 'Matched Users',
                func: () => setMatchesModalOpen(true)
            })
        }
        if (isPosting && !isWidget) {
            options.push({
                icon: <UserPlus className="h-4 w-4" />,
                title: 'Direct Add Staff',
                func: () => setDirectAddOpen(true)
            })
        }
        if (!isPosting && !isWidget) {
            options.push({
                icon: <UserPlus className="h-4 w-4" />,
                title: 'Direct Add Staff',
                func: () => setDirectAddOpen(true)
            }, {
                icon: <ShieldOff className="h-4 w-4" />,
                title: 'Blocked Relationships',
                func: () => setBlockedRelationshipsOpen(true)
            });
        }
        if (!isWidget) {
            options.push({
                icon: <Notebook className="h-4 w-4" />,
                title: 'Client File',
                func: openClientFile
            });
        }
        if (isPosting && !isWidget && accessControl.canWrite('staffing_dashboard')) {
            options.push({
                icon: <XCircle className="h-4 w-4 text-destructive" />,
                title: 'Close',
                func: () => setCloseDialogState('confirm')
            });
        }
        return options;
    }, [isPosting, isMatch, isWidget, openClientFile, accessControl]);

    return (
        <>
            {isWidget ? (
                <span className="min-h-[48px] px-3 flex items-center">{toInitials(clientName)}</span>
            ) : (
            <DropdownMenu onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="min-h-[48px] px-3 rounded"
                    >
                        {clientName}
                        {menuOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48">
                    {menuOptions.map(({ icon, title, func }) => (
                        <DropdownMenuItem key={title} onClick={func}>
                            {icon}
                            {title}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            )}
            <NewPostingModal
                open={postingModalOpen}
                onOpenChange={setPostingModalOpen}
                clientId={clientId}
                clientName={clientName}
            />
            <ClientPostingsModal
                open={viewPostingsOpen}
                onOpenChange={setViewPostingsOpen}
                clientId={clientId}
                clientName={clientName}
            />
            <StaffMatchesModal
                open={matchesModalOpen}
                onOpenChange={setMatchesModalOpen}
                clientId={clientId}
                clientName={clientName}
                serviceType={serviceType}
            />
            <NotesModal
                open={notesModalOpen}
                onOpenChange={setNotesModalOpen}
                identifier={clientId.toString()}
                userType={'client'}
                displayName={clientName ?? ''}
                onCountChange={() => { }}
            />
            <BlockedRelationshipsModal
                open={blockedRelationshipsOpen}
                onOpenChange={setBlockedRelationshipsOpen}
                clientId={clientId}
                clientName={clientName}
            />
            <DirectAddStaffModal
                open={directAddOpen}
                onOpenChange={setDirectAddOpen}
                clientId={clientId}
                clientName={clientName}
                postingId={postingId}
                serviceType={serviceType}
            />
            <Dialog open={closeDialogState !== null} onOpenChange={(open) => { if (!open && closeDialogState !== 'loading') setCloseDialogState(null); }}>
                <DialogContent>
                    {closeDialogState === 'confirm' && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Close Posting #{postingId}?</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-muted-foreground">This will close the posting and all associated child processes. This action cannot be undone.</p>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setCloseDialogState(null)}>Cancel</Button>
                                <Button variant="destructive" onClick={handleClosePosting}>Close Posting</Button>
                            </DialogFooter>
                        </>
                    )}
                    {closeDialogState === 'loading' && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Closing Posting...</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-muted-foreground">Please wait.</p>
                        </>
                    )}
                    {closeDialogState === 'success' && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Posting Closed</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-muted-foreground">Posting #{postingId} and its child processes have been successfully closed.</p>
                            <DialogFooter>
                                <Button onClick={() => setCloseDialogState(null)}>Done</Button>
                            </DialogFooter>
                        </>
                    )}
                    {closeDialogState === 'error' && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Failed to Close Posting</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-muted-foreground">{closeError}</p>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setCloseDialogState(null)}>Dismiss</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ClientStaffingContextMenu;
